import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import CTAButton from '../../../../CTAButton';
import OcLabel from '../../../../ComponentLibrary/input-elements/oc-label';
import { connectLocation } from '../../../../location/LocationConnectors';
import { useTRC } from '../../store';
import { QuestionWrapper } from '../../styles';
import generateResultsPdf from '../WallsAboveGrade/pdfResults';
import CalculatorLayout from '../shared/CalculatorLayout';
import Footer from '../shared/Footer';
import ResultsBlock from '../shared/ResultsBlock';
import {
  AIR_FILM_EXTERIOR_RSI,
  insulationOptions,
  INTERIOR_GWB_RSI_PER_MM,
  getOnCenterSpacingOptions,
  resetResults,
  roundOff,
  toRSIFloorsOverUnheatedSpace,
  validateInputs,
} from '../shared/constants';
import { DropdownField, RadioList, Section, YesNo } from '../shared/formControls';
import { smoothScrollIntoView } from '../shared/scroll';

/* ----------------------------- Constants ----------------------------- */

const WOOD_STRAP_BEHIND_GWB_RSI = 0.15;

const AIR_FILM_INTERIOR_RSI = 0.11;

export const ROOF_RSIF = { '2x4': 0.757, '2x6': 1.19, '2x8': 1.56, '2x10': 2, '2x12': 2.43 };

const AF_BY_SPACING_ROOF = { 304: 0.145, 406: 0.13, 488: 0.115, 610: 0.1 };

const getFramingTypeOptions = t => ({
  '2x4': t('thermal_calculator.roofs_cathedral.wood_rafters_joists_2_4'),
  '2x6': t('thermal_calculator.roofs_cathedral.wood_rafters_joists_2_6'),
  '2x8': t('thermal_calculator.roofs_cathedral.wood_rafters_joists_2_8'),
  '2x10': t('thermal_calculator.roofs_cathedral.wood_rafters_joists_2_10'),
  '2x12': t('thermal_calculator.roofs_cathedral.wood_rafters_joists_2_12'),
});

// Cavity (nominal) insulation choices (RSI)
const rsiRange = {
  '2x4': { values: [12, 14] },
  '2x6': { values: [12, 14, 19, 20, 22, 24] },
  '2x8': { values: [12, 14, 19, 20, 22, 24, 28] },
  '2x10': { values: [12, 14, 19, 20, 22, 24, 28, 31] },
  '2x12': { values: [12, 14, 19, 20, 22, 24, 28, 31, 35, 40] },
};

const rsiOptions = insulationOptions(rsiRange, toRSIFloorsOverUnheatedSpace);

// Defaults for this calc’s inputs
const DEFAULTS = {
  framingType: '2x4',
  onCenterSpacing: '304',
  cavityRSI: '',
  gypsumBoardThickness: '12.7',
  hasWoodStrapping: true,
  hasVapourBarrier: true,
};

const RoofCathedralCeilings = ({ calcId, t }) => {
  const { state, setResults: publishResults, setInputs: publishInputs } = useTRC();
  const { province, region, hdd } = state.location;
  const hasLocation = !!(province && region);
  const framingTypeOptions = getFramingTypeOptions(t);
  const onCenterSpacingOptions = getOnCenterSpacingOptions(t);

  // Hydrate local inputs from store (if present), else defaults
  const savedInputs = state.inputsByCalc?.[calcId] || {};
  const [framingType, setFramingType] = useState(savedInputs.framingType ?? DEFAULTS.framingType);
  const [onCenterSpacing, setOnCenterSpacing] = useState(savedInputs.onCenterSpacing ?? DEFAULTS.onCenterSpacing);
  const [cavityRSI, setCavityRSI] = useState(savedInputs.cavityRSI ?? DEFAULTS.cavityRSI);
  const [gypsumBoardThickness, setGypsumBoardThickness] = useState(
    savedInputs.gypsumBoardThickness ?? DEFAULTS.gypsumBoardThickness
  );
  const [hasWoodStrapping, setHasWoodStrapping] = useState(savedInputs.hasWoodStrapping ?? DEFAULTS.hasWoodStrapping);
  const [hasVapourBarrier, setHasVapourBarrier] = useState(savedInputs.hasVapourBarrier ?? DEFAULTS.hasVapourBarrier);

  // Persist inputs to store whenever any change (debounced by React batching)
  useEffect(() => {
    publishInputs({
      calcId,
      inputs: {
        framingType,
        onCenterSpacing,
        cavityRSI,
        gypsumBoardThickness,
        hasWoodStrapping,
        hasVapourBarrier,
      },
    });
  }, [
    calcId,
    framingType,
    onCenterSpacing,
    cavityRSI,
    gypsumBoardThickness,
    hasWoodStrapping,
    hasVapourBarrier,
    publishInputs,
  ]);

  // Read results from the store so they persist across tab switches
  const results = state.resultsByCalc?.[calcId]?.results || null;

  const resultsRef = useRef(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!error) return;
    const timeout = setTimeout(() => setError(null), 5000);
    // eslint-disable-next-line
    return () => clearTimeout(timeout);
  }, [error]);

  // Overview rows for the top “summary” table
  const rowsForTable = [
    { label: t('thermal_calculator.effective_assembly_rsi'), rsi: 'effectiveAssemblyRSI' },
    { label: t('thermal_calculator.center_of_cavity'), rsi: 'centerOfCavityRSI' },
    { label: t('thermal_calculator.installed_insulation_rsi'), rsi: 'installedInsulationRSI' },
    {
      label: t('thermal_calculator.vented_air_space'),
      rsi: 'ventedAirSpaceRSI',
      noR: true,
    }, // included as 0.00 in overview
  ];

  /* ----------------------------- Calculator ----------------------------- */

  const calculate = () => {
    // Fractions
    const Af = AF_BY_SPACING_ROOF[onCenterSpacing] ?? 0.145;
    const Ac = Math.max(0, 1 - Af);

    // Path RSIs
    const RSIf = ROOF_RSIF[framingType] ?? 0;
    const RSIc = parseFloat(cavityRSI || '0');

    // Continuous layers (numbers, not strings)
    const strapRSI = hasWoodStrapping ? WOOD_STRAP_BEHIND_GWB_RSI : 0;
    const gypsumRSI = roundOff(INTERIOR_GWB_RSI_PER_MM * parseFloat(gypsumBoardThickness || '0'));
    const interiorFilmRSI = AIR_FILM_INTERIOR_RSI;
    const vapourBarrierRSI = hasVapourBarrier ? 0 : 0; // explicitly 0

    const roofSheathingRSI = 0;
    const ventedAirSpaceRSI = AIR_FILM_EXTERIOR_RSI;

    const continuousTotalRSI =
      roofSheathingRSI + ventedAirSpaceRSI + gypsumRSI + vapourBarrierRSI + strapRSI + interiorFilmRSI;

    // Parallel (frame/cavity) through joist depth
    const RSIparallel = RSIf > 0 && RSIc > 0 ? 1 / (Af / RSIf + Ac / RSIc) : 0;

    // Effective assembly + center-of-cavity
    const effectiveAssemblyRSI = RSIparallel + continuousTotalRSI;

    // Center of cavity = cavity path + continuous
    const centerOfCavityRSI = RSIc + continuousTotalRSI;

    // Installed insulation (nominal) for this assembly is the cavity nominal
    const installedInsulationRSI = RSIc;
    const nominalRsiText = rsiOptions[framingType].find(opt => String(opt.value) === String(cavityRSI))?.text || '';

    // Layers for PDF (continuous + non-continuous)
    const layers = {
      continuous: [
        { label: `${t('thermal_calculator.roofs_below_attics.roof_sheathing')}*`, rsi: roofSheathingRSI },
        { label: `${t('thermal_calculator.roofs_below_attics.exterior_air_film')}²`, rsi: ventedAirSpaceRSI },
        { label: t('thermal_calculator.roofs_below_attics.vapour_retarder'), rsi: vapourBarrierRSI },
        ...(hasWoodStrapping
          ? [{ label: t('thermal_calculator.roofs_below_attics.strap_air_up'), rsi: strapRSI }]
          : []),
        {
          // eslint-disable-next-line
          label: `${t('thermal_calculator.floors_over_unheated_spaces.gypsum_board')}: ${parseFloat(gypsumBoardThickness)} mm (${gypsumBoardThickness === '16' ? '5/8"' : '1/2"'
          })`,
          rsi: gypsumRSI,
        },
        { label: t('thermal_calculator.roofs_below_attics.interior_air_film'), rsi: interiorFilmRSI },
      ],
      nonContinuous: {
        // eslint-disable-next-line max-len
        label: `${t('thermal_calculator.roofs_cathedral.ceiling_joist')}: ${framingTypeOptions[framingType]}, ${
          onCenterSpacingOptions[onCenterSpacing]
        } ${t('thermal_calculator.floors_over_unheated_spaces.on_center')}, ${nominalRsiText} ${t(
          'thermal_calculator.roofs_cathedral.nominal_insulation'
        )}`,
        rsi: RSIparallel,
      },
    };

    return {
      Af,
      Ac,
      RSIf,
      RSIc,
      RSIparallel,
      effectiveAssemblyRSI,
      centerOfCavityRSI,
      installedInsulationRSI,
      ventedAirSpaceRSI,
      layers,
    };
  };

  const onCalculate = () => {
    if (!validateInputs({ province, region, setError, cavityRSI, t })) {
      smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
      // do not change persisted inputs; just report error
      return;
    }
    // inputs are valid, clear any previous error
    setError(null);

    // perform calculation
    const res = calculate();
    publishResults({
      calcId,
      results: res,
      meta: { title: t('thermal_calculator.titles.roofs_cathedral') },
    });
    smoothScrollIntoView(resultsRef, { block: 'start', behavior: 'smooth', offset: 0 });
  };

  const onReset = () => {
    // clear results in store
    resetResults(calcId, t('thermal_calculator.titles.roofs_cathedral'), publishResults);

    // reset local inputs
    setFramingType(DEFAULTS.framingType);
    setOnCenterSpacing(DEFAULTS.onCenterSpacing);
    setCavityRSI(DEFAULTS.cavityRSI);
    setGypsumBoardThickness(DEFAULTS.gypsumBoardThickness);
    setHasWoodStrapping(DEFAULTS.hasWoodStrapping);
    setHasVapourBarrier(DEFAULTS.hasVapourBarrier);
    setError(null);

    // clear inputs for this calc in store
    publishInputs({ calcId, inputs: null });
  };

  /* ----------------------------- UI ----------------------------- */

  const sidebar = (
    <>
      <Section title={t('thermal_calculator.non_continuous_layers')}>
        <div>
          <OcLabel forId={`framing-type-${calcId}`} text={t('thermal_calculator.framing_type')} />
          <DropdownField
            id={`framing-type-${calcId}`}
            name="framing-type"
            value={framingType}
            onChange={e => setFramingType(e.target.value)}
            style={{ width: '100%', marginTop: 6, padding: '8px 10px' }}
            options={Object.entries(framingTypeOptions).map(([value, text]) => ({ value, text }))}
          />
        </div>

        <div>
          <OcLabel
            forId={`on-center-spacing-${calcId}`}
            text={t('thermal_calculator.floors_over_unheated_spaces.framing_on_center')}
          />
          <DropdownField
            id={`on-center-spacing-${calcId}`}
            name="framing-on-center"
            value={onCenterSpacing}
            onChange={e => setOnCenterSpacing(e.target.value)}
            style={{ width: '100%', marginTop: 6, padding: '8px 10px' }}
            options={Object.entries(onCenterSpacingOptions).map(([value, text]) => ({ value, text }))}
          />
        </div>

        <div>
          <OcLabel
            forId={`cavityRSI-${calcId}`}
            text={t('thermal_calculator.floors_over_unheated_spaces.cavity_nominal_rsi')}
          />
          <DropdownField
            id={`cavityRSI-${calcId}`}
            name="cavity-nominal-rsi"
            value={cavityRSI}
            onChange={e => setCavityRSI(e.target.value)}
            style={{ width: '100%', marginTop: 6, padding: '8px 10px' }}
            options={rsiOptions[framingType] || []}
          />
        </div>
      </Section>

      <Section title={t('thermal_calculator.continuous_layers')}>
        <QuestionWrapper>
          <OcLabel text={t('thermal_calculator.roofs_below_attics.gypsum_board_thickness')} />
          <RadioList
            name={`gypsum-thickness-${calcId}`}
            value={gypsumBoardThickness}
            onChange={setGypsumBoardThickness}
            options={[
              { value: '12.7', label: t('thermal_calculator.dimensions.gypsum_12_7') },
              { value: '16', label: t('thermal_calculator.dimensions.gypsum_16') },
            ]}
          />
        </QuestionWrapper>

        <div>
          <OcLabel text={t('thermal_calculator.roofs_below_attics.wood_strap_gypsum')} />
          <YesNo name={`wood-strapping-${calcId}`} value={hasWoodStrapping} onChange={setHasWoodStrapping} />
        </div>

        <div>
          <OcLabel text={t('thermal_calculator.roofs_below_attics.vapour_barrier')} />
          <YesNo name={`vapour-barrier-${calcId}`} value={hasVapourBarrier} onChange={setHasVapourBarrier} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <CTAButton
            onClick={onCalculate}
            disabled={!hasLocation}
            text={t('thermal_calculator.calculate_r_value')}
            style={{ minWidth: '200px' }}
            data-track="calculate"
          />
          <CTAButton
            element="button"
            type="button"
            onClick={onReset}
            text={t('thermal_calculator.reset')}
            style={{
              minWidth: '100px',
              padding: '8px 16px',
              border: '1px solid #ccc',
              background: '#fff',
              color: '#000',
            }}
          />
        </div>
      </Section>
    </>
  );

  const handleDownload = () => {
    if (!results) return;
    generateResultsPdf({
      calcId,
      province,
      region,
      hdd,
      assemblyHeading: t('thermal_calculator.titles.roofs_cathedral'),
      tableTitle: t('thermal_calculator.entire_assembly'),
      tableSubtitle: t('thermal_calculator.effective_thermal_resistance'),
      rowsConfig: rowsForTable,
      results,
      footnotes: [
        `1. ${t('thermal_calculator.footnotes.air_cavity')}`,
        `2. ${t('thermal_calculator.footnotes.vented_roof')}`,
      ],
      t,
    });
  };

  const resultsPane = (
    <div ref={resultsRef}>
      <ResultsBlock
        calcId={calcId}
        province={province}
        region={region}
        hdd={hdd}
        error={error}
        results={results}
        rows={rowsForTable}
      />
      <Footer results={results} onDownload={handleDownload} />
    </div>
  );

  return <CalculatorLayout sidebar={sidebar} results={resultsPane} />;
};

RoofCathedralCeilings.propTypes = {
  calcId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default connectLocation(RoofCathedralCeilings);
